import { useEffect, useRef } from 'react'

import { CROSS_JOBS, CROSS_TALENTS } from '../../content'
import { useFrame } from '../../engine'
import { anim, clamp, lerp, seg, smooth } from '../../motion'
import { Avatar } from '../../primitives'
import { Line } from '../kit'

// Kamerafahrt durch die Kreuzmatching-Matrix: rein, raus, wieder rein
const LABEL_W = 200
const COL_W = 190
const ROW_H = 112
const HEAD_H = 64
const GAP = 12
const W = LABEL_W + CROSS_JOBS.length * COL_W
const H = HEAD_H + CROSS_TALENTS.length * ROW_H

const LENA = CROSS_TALENTS.findIndex((t) => t.highlight)
const BEST = CROSS_TALENTS[LENA].values.indexOf(
  Math.max(...CROSS_TALENTS[LENA].values)
)

const cellCenter = (r, c) => ({
  x: LABEL_W + c * COL_W + (COL_W - GAP) / 2,
  y: HEAD_H + r * ROW_H + (ROW_H - GAP) / 2
})

// Kamerapositionen: Zoom auf Vertrieb, Gesamtbild, Zoom auf den besten Match
const SHOTS = [
  { ...cellCenter(LENA, 0), z: 3.2 },
  { x: W / 2, y: H / 2, z: 1 },
  { ...cellCenter(LENA, BEST), z: 2.6 }
]

function camera(l) {
  const out = smooth(seg(l, 0.14, 0.38))
  const back = smooth(seg(l, 0.6, 0.8))
  const [a, b] = back > 0 ? [SHOTS[1], SHOTS[2]] : [SHOTS[0], SHOTS[1]]
  const t = back > 0 ? back : out
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    z: Math.exp(lerp(Math.log(a.z), Math.log(b.z), t))
  }
}

const PALE = [235, 239, 253]
const BLUE = [64, 123, 255]
const heat = (v) => {
  const t = clamp((v - 50) / 45)
  return `rgb(${PALE.map((ch, i) => Math.round(lerp(ch, BLUE[i], t))).join(',')})`
}

export default function CinCross() {
  const stage = useRef(null)
  const world = useRef(null)
  const last = useRef(0)

  const place = (l) => {
    const host = stage.current
    if (!host || !world.current) return
    const fit = Math.min(
      (host.clientWidth * 0.86) / W,
      (host.clientHeight * 0.6) / H
    )
    const cam = camera(l)
    const s = fit * cam.z
    const cx = host.clientWidth / 2
    const cy = host.clientHeight * 0.42
    world.current.style.transform = `translate(${cx - cam.x * s}px, ${cy - cam.y * s}px) scale(${s})`
  }

  useFrame((l) => {
    last.current = l
    place(l)
  })

  useEffect(() => {
    const ro = new ResizeObserver(() => place(last.current))
    if (stage.current) ro.observe(stage.current)
    return () => ro.disconnect()
  }, [])

  return (
    <div ref={stage} className='cin-full cin-cross'>
      <div
        ref={world}
        className='cin-cross__world'
        style={{ width: W, height: H }}
      >
        {CROSS_JOBS.map((job, c) => (
          <div
            key={job}
            className='cin-cross__head'
            style={{ left: LABEL_W + c * COL_W, width: COL_W - GAP }}
          >
            {job}
          </div>
        ))}
        {CROSS_TALENTS.map((t, r) => (
          <div key={t.name}>
            <div
              className='cin-cross__name'
              style={{ top: HEAD_H + r * ROW_H, height: ROW_H - GAP }}
            >
              <Avatar
                initials={t.name[0]}
                color={t.highlight ? 'pink' : '#B2B4B6'}
                size={36}
              />
              {t.name}
            </div>
            {t.values.map((v, c) => {
              const lenaVertrieb = r === LENA && c === 0
              const order = r * CROSS_JOBS.length + c
              return (
                <div
                  key={c}
                  className='cin-cross__cell'
                  style={{
                    left: LABEL_W + c * COL_W,
                    top: HEAD_H + r * ROW_H,
                    width: COL_W - GAP,
                    height: ROW_H - GAP,
                    background: heat(v),
                    color: v >= 76 ? '#fff' : '#182033',
                    ...(lenaVertrieb
                      ? {}
                      : anim({
                          at: [0.24 + order * 0.008, 0.3 + order * 0.008],
                          s: 0.7,
                          y: 0
                        }))
                  }}
                >
                  {v}&nbsp;%
                  {r === LENA && c === BEST && (
                    <span
                      className='cin-cross__best'
                      style={anim({ at: [0.72, 0.8], s: 1.2, y: 0 })}
                    />
                  )}
                </div>
              )
            })}
          </div>
        ))}
      </div>

      <div className='cin-cross__copy'>
        <Line at={[-1, 0]} out={[0.14, 0.2]} className='cin-title'>
          64&nbsp;% für den Vertrieb.{' '}
          <span className='cin-muted'>Knapp daneben.</span>
        </Line>
        <Line at={[0.24, 0.32]} out={[0.56, 0.62]} className='cin-title'>
          Ein Ergebnis. Alle Stellen.{' '}
          <span className='cin-muted'>Ohne neues Assessment.</span>
        </Line>
        <Line at={[0.7, 0.78]} className='cin-title'>
          91&nbsp;% für Customer Success.{' '}
          <span className='cin-muted'>Gefunden statt aussortiert.</span>
        </Line>
      </div>
    </div>
  )
}
